import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../utils/api';

interface UserData {
  id: string;
  username: string;
  email: string;
  role: string;
  [key: string]: any;
}

interface UserContextType {
  user: UserData | null;
  setUser: (user: UserData | null) => void;
  isLoading: boolean;
  isModerator: () => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeUser = () => {
      try {
        if (authService.isAuthenticated()) {
          const userData = authService.getUserData();
          setUser(userData);
        }
      } catch (error) {
        console.error('Failed to initialize user:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const handleAuthChange = () => {
      if (authService.isAuthenticated()) {
        const userData = authService.getUserData();
        setUser(userData);
      } else {
        setUser(null);
      }
    };

    authService.addAuthChangeListener(handleAuthChange);
    initializeUser();

    return () => {
      authService.removeAuthChangeListener(handleAuthChange);
    };
  }, []);

  const isModerator = () => {
    return authService.isAuthenticated() && user !== null;
  };

  return (
    <UserContext.Provider value={{ user, setUser, isLoading, isModerator }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
